import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { signIn, snsLink, snsSelect } from '../../Module/Auth/Presentation/authActions';
import { selectAuthState } from '../../Module/Auth/Presentation/authSelectors';
import { PATHS } from '../../routes/paths';
import { getAccessToken } from '../../core/api/tokenStorage';
import {
  SNS_STATE_STORAGE_KEY,
  SNS_TICKET_INVALID_CODE,
  getSnsErrorCode,
  type SnsLoginState,
  type SnsSignupState,
} from '../../Utils/snsLogin';

const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const DEFAULT_KAKAO_DONOR_REDIRECT_URL = 'https://missionfund.org/login_ok_kakaouser.asp';

declare global {
  interface Window {
    Kakao: any;
  }
}

const createSnsState = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

// emailOnly: 카카오 인증 없이 이메일·비밀번호로 로그인하는 임시 안내용 화면 (/login/email)
export const useLogin = (emailOnly = false) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useAppSelector(selectAuthState);

  // 카카오 인증을 마치고 돌아온 경우 (SnsCallback 이 넘겨 준 값). 없으면 카카오 로그인 버튼만 보여 준다
  const sns = (location.state as { sns?: SnsLoginState } | null | undefined)?.sns;

  // If already logged in (userId exists), skip login screen
  useEffect(() => {
    try {
      const userId = window.localStorage.getItem('missionfund.userId');
      const accessToken = getAccessToken();
      if (userId && accessToken) navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
    } catch {
      // ignore
    }
  }, [navigate]);

  const [loginType, setLoginType] = useState<'fundraiser' | 'donor'>(() => {
    if (typeof window === 'undefined') return 'fundraiser';
    const params = new URLSearchParams(window.location.search);
    return params.get('loginType') === 'donor' ? 'donor' : 'fundraiser';
  });
  const [memberType, setMemberType] = useState<'individual' | 'group'>('individual');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [snsError, setSnsError] = useState('');
  const kakaoSdkLoadingRef = useRef<Promise<any> | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('loginType') === 'donor') {
      setLoginType('donor');
    }
  }, [location.search]);

  const ensureKakaoSdk = useCallback(async () => {
    if (window.Kakao?.Auth) {
      if (!window.Kakao.isInitialized()) {
        window.Kakao.init(KAKAO_KEY);
      }
      return window.Kakao;
    }

    if (kakaoSdkLoadingRef.current) {
      return kakaoSdkLoadingRef.current;
    }

    kakaoSdkLoadingRef.current = new Promise((resolve, reject) => {
      const finalize = () => {
        if (!window.Kakao?.Auth) {
          reject(new Error('Kakao SDK not available'));
          return;
        }
        if (!window.Kakao.isInitialized()) {
          window.Kakao.init(KAKAO_KEY);
        }
        resolve(window.Kakao);
      };

      const sdkSrc = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${sdkSrc}"]`);
      if (existingScript) {
        if (window.Kakao?.Auth) {
          finalize();
          return;
        }
        existingScript.addEventListener('load', finalize, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK script')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = sdkSrc;
      script.crossOrigin = 'anonymous';
      script.onload = finalize;
      script.onerror = () => reject(new Error('Failed to load Kakao SDK script'));
      document.head.appendChild(script);
    }).catch((error) => {
      kakaoSdkLoadingRef.current = null;
      throw error;
    });

    return kakaoSdkLoadingRef.current;
  }, []);

  const handleKakaoDonorLogin = useCallback(async () => {
    const configuredRedirectUrl = (process.env.REACT_APP_KAKAO_DONOR_REDIRECT_URL as string | undefined)?.trim();
    const redirectUrl = configuredRedirectUrl || DEFAULT_KAKAO_DONOR_REDIRECT_URL;

    try {
      const kakao = await ensureKakaoSdk();
      kakao.Auth.authorize({ redirectUri: redirectUrl });
    } catch (error) {
      console.error('Failed to start Kakao authorize flow:', error);
      window.location.assign(
        `https://kauth.kakao.com/oauth/authorize?client_id=${encodeURIComponent(KAKAO_KEY)}&redirect_uri=${encodeURIComponent(redirectUrl)}&response_type=code`,
      );
    }
  }, [ensureKakaoSdk]);

  // 모금자 카카오 로그인 시작 — 인증 후 /auth/kakao/callback 으로 돌아온다
  const handleKakaoUserLogin = useCallback(async () => {
    const state = createSnsState();
    try {
      window.sessionStorage.setItem(SNS_STATE_STORAGE_KEY, state);
    } catch {
      // ignore storage failures (콜백에서 state 불일치로 처리된다)
    }
    // on / devon / localhost 각자 자기 주소로 돌아온다 (sapi 에 보내는 redirect_uri 와 같아야 한다)
    const redirectUri = `${window.location.origin}${PATHS.SNS_KAKAO_CALLBACK}`;

    try {
      const kakao = await ensureKakaoSdk();
      kakao.Auth.authorize({ redirectUri, state });
    } catch (error) {
      console.error('Failed to start Kakao authorize flow:', error);
      window.location.assign(
        `https://kauth.kakao.com/oauth/authorize?client_id=${encodeURIComponent(KAKAO_KEY)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=${encodeURIComponent(state)}`,
      );
    }
  }, [ensureKakaoSdk]);

  // 카카오 인증 상태를 버리고 처음(카카오 로그인 버튼)으로 돌아간다
  const handleSnsReset = useCallback(() => {
    setSnsError('');
    setPassword('');
    navigate(PATHS.LOGIN, { replace: true, state: null });
  }, [navigate]);

  const getSnsErrorMessage = useCallback(
    (message?: string) => {
      if (!message) return '';
      switch (getSnsErrorCode(message)) {
        case 40130:
          return t('page.login.errors.invalidCredentials');
        case 40911:
          return t('page.login.sns.errors.linkedToOther');
        case 42910:
          return t('page.login.sns.errors.tooManyAttempts');
        case 40042:
        case 40131:
          return t('page.login.sns.errors.selectFailed');
        case SNS_TICKET_INVALID_CODE:
          return t('page.login.sns.errors.ticketExpired');
        default:
          break;
      }
      if (message.includes('Network Error') || message.includes('Failed to fetch')) {
        return t('page.login.errors.network');
      }
      return t('page.login.errors.default');
    },
    [t],
  );

  const handleSnsFailure = useCallback(
    (error: unknown) => {
      const message = typeof error === 'string' ? error : (error as Error)?.message;
      // 티켓이 만료되면 카카오 인증부터 다시 해야 한다
      if (getSnsErrorCode(message) === SNS_TICKET_INVALID_CODE) {
        window.alert(t('page.login.sns.errors.ticketExpired'));
        handleSnsReset();
        return;
      }
      setSnsError(getSnsErrorMessage(message));
    },
    [getSnsErrorMessage, handleSnsReset, t],
  );

  // 연결된 계정 선택 → 로그인
  const handleSelectAccount = useCallback(
    async (userId: string) => {
      if (!sns) return;
      setSnsError('');
      try {
        await dispatch(snsSelect({ ticket: sns.ticket, user_id: userId })).unwrap();
        navigate(PATHS.PROJECT_MANAGEMENT);
      } catch (error) {
        handleSnsFailure(error);
      }
    },
    [dispatch, handleSnsFailure, navigate, sns],
  );

  // 카카오 인증 없이 이메일+비밀번호로 로그인 (카카오 계정과 연결하지 않는다)
  const handleEmailLogin = useCallback(async () => {
    setSnsError('');
    try {
      const partner = (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF';
      await dispatch(signIn({ user_email: email.trim(), password, partner })).unwrap();
      navigate(PATHS.PROJECT_MANAGEMENT);
    } catch (error) {
      const message = (typeof error === 'string' ? error : (error as Error)?.message) || '';
      if (message.includes('Incorrect') || message.includes('User not found')) {
        setSnsError(t('page.login.errors.invalidCredentials'));
      } else if (message.includes('Network Error') || message.includes('Failed to fetch')) {
        setSnsError(t('page.login.errors.network'));
      } else {
        setSnsError(t('page.login.errors.default'));
      }
    }
  }, [dispatch, email, navigate, password, t]);

  // 이메일+비밀번호 로그인 → 카카오 계정 연결 → 로그인
  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (emailOnly) {
        await handleEmailLogin();
        return;
      }
      if (!sns) return;
      setSnsError('');
      try {
        await dispatch(snsLink({ ticket: sns.ticket, user_email: email.trim(), password })).unwrap();
        navigate(PATHS.PROJECT_MANAGEMENT);
      } catch (error) {
        handleSnsFailure(error);
      }
    },
    [dispatch, email, emailOnly, handleEmailLogin, handleSnsFailure, navigate, password, sns],
  );

  // 카카오 인증 정보를 가지고 회원가입 화면으로
  const handleSnsSignup = useCallback(() => {
    if (!sns) return;
    const snsSignup: SnsSignupState = { ticket: sns.ticket, profile: sns.profile };
    navigate(PATHS.SIGNUP, { state: { snsSignup } });
  }, [navigate, sns]);

  const isBusy = status === 'loading';

  return {
    loginType,
    setLoginType,
    memberType,
    setMemberType,
    email,
    setEmail,
    password,
    setPassword,
    handleLogin,
    handleKakaoDonorLogin,
    handleKakaoUserLogin,
    handleSelectAccount,
    handleSnsSignup,
    handleSnsReset,
    sns,
    isBusy,
    error: snsError,
  };
};
